import { Grid } from '../../core/Grid.js';
import { splitEntries } from '../stateSerialization.js';
import {
    Position,
    Direction,
    positionKey,
    isInside
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';

export interface BeamSegment {
    from: Position;
    to: Position;
    distance: number; // Distance at start of segment
    beamId: number; // ID to track which beam this segment belongs to
}

export interface TerminalBeam {
    originPosition: Position; // The splitter/digit where this beam originated
    exitDirection: Direction; // Direction the beam was heading when it exited
    beamId: number; // ID for color lookup
}

export interface DigitHit {
    digit: number;
    position: Position;
    distance: number;
}

/** The light's route from the entry to one digit — see `getSelectedBeamRoute`. */
export interface BeamRoute {
    segments: BeamSegment[];
    teleports: Array<{ from: Position; to: Position }>;
}

export interface BeamSimulation {
    segments: BeamSegment[];
    digitsHit: DigitHit[];
    allTerminated: boolean;
    hasInfiniteLoop: boolean;
    hasDigitHitTwice: boolean;
    digitHitTwice: number | null;
    terminalBeams: TerminalBeam[]; // Beams that exit without hitting another splitter
    terminalBeamIds: Set<number>; // IDs of beams that go straight to edge (don't draw these)
    splittersInLoop: Set<string>; // Position keys of splitters involved in infinite loops
    teleportSegments: Array<{ from: Position; to: Position }>; // Vortex gate teleport paths
    beamColor: Map<number, string>; // beamId → 'teal' | 'amber' | 'red'
    beamParent: Map<number, number>; // child beamId → parent beamId
    beamSplitPosition: Map<number, string>; // beamId → positionKey of origin split
}

interface ActiveBeam {
    position: Position;
    direction: Direction;
    distance: number;
    beamId: number;
    originPosition: Position; // Where this beam originated (last splitter/digit)
    originDirection: Direction; // Direction from origin
    splitterPath: string[]; // Chain of splitter/digit positions visited by this beam's lineage
    gatePath: Set<string>; // Gate entry keys visited by this beam's lineage (for cycle detection)
}

export class SplittingGame {
    protected grid: Grid;
    private splitters: Set<string> = new Set();
    protected cachedSimulation: BeamSimulation | null = null;

    constructor(grid: Grid) {
        this.grid = grid;
    }

    protected get gs() { return this.grid.serializer; }

    getGrid(): Grid {
        return this.grid;
    }

    getSplitters(): Set<string> {
        return new Set(this.splitters);
    }

    hasSplitterAt(pos: Position): boolean {
        return this.splitters.has(positionKey(pos));
    }

    toggleSplitter(pos: Position): void {
        // Cannot place splitter on digit
        if (this.grid.hasDigitAt(pos)) return;

        const key = positionKey(pos);
        if (this.splitters.has(key)) {
            this.splitters.delete(key);
        } else {
            this.splitters.add(key);
        }
        this.cachedSimulation = null;
    }

    reset(): void {
        this.splitters.clear();
        this.cachedSimulation = null;
        this.selectedDigit = null;
    }

    // ── Route highlight selection (transient view state, like Turns) ────

    protected selectedDigit: number | null = null;

    /** Toggle the highlight of the route the light takes to reach a digit. */
    toggleSelectedDigit(digit: number): void {
        this.selectedDigit = this.selectedDigit === digit ? null : digit;
    }

    getSelectedDigit(): number | null {
        return this.selectedDigit;
    }

    clearSelectedDigit(): void {
        this.selectedDigit = null;
    }

    /**
     * The route the light travelled from the entry to the selected digit: every
     * beam segment on the way there and nothing else. Empty when no digit is
     * selected, or when the selected one isn't reached at all.
     *
     * A beam gets a fresh id at every split, and `beamParent` records which beam it
     * came out of — so the route is the beam that arrived at the digit plus its
     * ancestors, back to the one that entered at the top-left corner. Every segment
     * of an ancestor is on the route: a beam runs straight from the split that made
     * it to the split that ends it, and the child was made by that second split.
     */
    getSelectedBeamRoute(): BeamRoute {
        const empty: BeamRoute = { segments: [], teleports: [] };
        if (this.selectedDigit === null) return empty;

        const sim = this.simulate();
        const hit = sim.digitsHit.find(h => h.digit === this.selectedDigit);
        if (!hit) return empty;

        // The segment that arrived: it ends on the digit, one step short of the
        // distance recorded there. A digit reached twice has two such segments, and
        // the distance is what picks out the arrival that counted.
        const arriving = sim.segments.filter(s =>
            s.to.row === hit.position.row && s.to.col === hit.position.col);
        const last = arriving.find(s => s.distance + 1 === hit.distance) ?? arriving[0];
        if (!last) return empty;

        const lineage = new Set<number>();
        let id: number | undefined = last.beamId;
        while (id !== undefined && !lineage.has(id)) {
            lineage.add(id);
            id = sim.beamParent.get(id);
        }

        const segments = sim.segments.filter(s => lineage.has(s.beamId));

        // Teleports carry no beam id — a gate the route runs into and out of is one
        // whose both ends the route touches.
        const onRoute = new Set<string>();
        for (const s of segments) {
            onRoute.add(positionKey(s.from));
            onRoute.add(positionKey(s.to));
        }
        const teleports = sim.teleportSegments.filter(t =>
            onRoute.has(positionKey(t.from)) && onRoute.has(positionKey(t.to)));

        return { segments, teleports };
    }

    /**
     * Jumpstart a solution: place splitters so the entry beam reaches the first
     * digit first (shortest distance). When digit 1's column is clear above it,
     * a single splitter at row 0 drops the beam straight onto it; otherwise the
     * beam is routed down a nearby clear column and turned in along digit 1's
     * row. Verified against the beam simulator; prefers a loop-free, single-hit
     * result. May fail on a few crowded grids.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const min = this.grid.minDigit;
        const d1 = this.grid.getDigitPosition(min);
        if (!d1) return false;

        const size = this.grid.size;
        const r1 = d1.row, c1 = d1.col;
        const digAt = (r: number, c: number) => this.grid.hasDigitAt({ row: r, col: c });
        const colClearAbove = (c: number) => {
            for (let r = 0; r < r1; r++) if (digAt(r, c)) return false;
            return true;
        };

        const candidates: Position[][] = [];
        // Single splitter straight above digit 1 (top row is always digit-free).
        candidates.push([{ row: 0, col: c1 }]);
        // Routes: drop down a nearby clear column, turn in along digit 1's row.
        const cols = Array.from({ length: size }, (_, k) => k)
            .filter(c => c !== c1)
            .sort((a, b) => Math.abs(a - c1) - Math.abs(b - c1));
        for (const cp of cols) {
            if (!colClearAbove(cp) || digAt(r1, cp)) continue;
            const lo = Math.min(cp, c1), hi = Math.max(cp, c1);
            let rowClear = true;
            for (let c = lo + 1; c < hi; c++) if (digAt(r1, c)) { rowClear = false; break; }
            if (!rowClear) continue;
            candidates.push([{ row: 0, col: cp }, { row: r1, col: cp }]);
        }

        let fallback: Position[] | null = null;
        for (const splitters of candidates) {
            this.reset();
            for (const p of splitters) this.toggleSplitter(p);
            const sim = this.simulate();
            if (!this.reachesFirstDigitFirst(sim, min)) continue;
            if (!sim.hasInfiniteLoop && !sim.hasDigitHitTwice) return true;
            if (!fallback) fallback = splitters;
        }

        if (fallback) {
            this.reset();
            for (const p of fallback) this.toggleSplitter(p);
            return true;
        }

        this.reset();
        return false;
    }

    /** True if the first digit is hit strictly before any other (sole shortest distance). */
    private reachesFirstDigitFirst(sim: BeamSimulation, min: number): boolean {
        const hits = sim.digitsHit;  // sorted by distance ascending
        if (hits.length === 0 || hits[0].digit !== min) return false;
        return hits.length === 1 || hits[1].distance > hits[0].distance;
    }

    // Get perpendicular directions
    private getPerpendicularDirections(dir: Direction): [Direction, Direction] {
        if (dir === 'N' || dir === 'S') {
            return ['W', 'E'];
        } else {
            return ['N', 'S'];
        }
    }

    /** Hook for vortex teleportation. Returns exit gate position, or null if no teleport. */
    protected checkVortexTeleport(_position: Position): Position | null {
        return null;
    }

    simulate(): BeamSimulation {
        if (this.cachedSimulation) return this.cachedSimulation;

        const segments: BeamSegment[] = [];
        const digitsHit: DigitHit[] = [];
        const terminalBeams: TerminalBeam[] = [];
        const terminalBeamIds = new Set<number>();
        const splittersInLoop = new Set<string>();
        const digitHitCount = new Map<number, number>();
        const splitterHitCount = new Map<string, number>(); // Track hits per splitter/digit
        const teleportSegments: Array<{ from: Position; to: Position }> = [];
        const beamParent = new Map<number, number>();
        const beamSplitPosition = new Map<number, string>();

        let nextBeamId = 0;
        const initialBeamId = nextBeamId++;

        // Start beam from top-left corner going right
        const activeBeams: ActiveBeam[] = [{
            position: { row: 0, col: 0 },
            direction: 'E',
            distance: 0,
            beamId: initialBeamId,
            originPosition: { row: 0, col: -1 }, // Entry point (outside grid)
            originDirection: 'E',
            splitterPath: [],
            gatePath: new Set()
        }];

        const maxIterations = this.grid.size * this.grid.size * 10;
        let iterations = 0;
        let hasInfiniteLoop = false;
        let hasDigitHitTwice = false;
        let digitHitTwice: number | null = null;

        while (activeBeams.length > 0 && iterations < maxIterations) {
            iterations++;

            const beam = activeBeams.shift()!;

            // Vortex teleportation check (before split check)
            const teleportExit = this.checkVortexTeleport(beam.position);
            if (teleportExit) {
                const entryKey = positionKey(beam.position);
                // Track gate + direction: same gate from a different direction is not a cycle
                const gateVisitKey = `${entryKey}:${beam.direction}`;
                if (beam.gatePath.has(gateVisitKey)) {
                    hasInfiniteLoop = true;
                    continue;
                }
                teleportSegments.push({ from: { ...beam.position }, to: { ...teleportExit } });
                // New beam ID so pre-teleport segments are preserved as non-terminal
                const oldBeamId = beam.beamId;
                beam.beamId = nextBeamId++;
                beamParent.set(beam.beamId, oldBeamId);
                beam.originPosition = { ...teleportExit };
                beam.originDirection = beam.direction;
                beam.position = teleportExit;
                beam.gatePath = new Set(beam.gatePath);
                beam.gatePath.add(gateVisitKey);
                // Fall through: exit gate has no digit/splitter, beam continues straight
            }

            // Check if current position causes a split (digit or splitter)
            const posKey = positionKey(beam.position);
            const digit = this.grid.getDigitAt(beam.position);
            const isDigit = digit !== null;
            const isSplitter = this.splitters.has(posKey);
            const shouldSplit = isDigit || isSplitter;

            // Record digit hit
            if (isDigit) {
                const count = (digitHitCount.get(digit) || 0) + 1;
                digitHitCount.set(digit, count);

                if (count === 1) {
                    digitsHit.push({
                        digit,
                        position: { ...beam.position },
                        distance: beam.distance
                    });
                } else if (count === 2 && !hasDigitHitTwice) {
                    hasDigitHitTwice = true;
                    digitHitTwice = digit;
                }
            }

            if (shouldSplit) {
                // Track splitter/digit hits for safety fallback
                const hitCount = (splitterHitCount.get(posKey) || 0) + 1;
                splitterHitCount.set(posKey, hitCount);

                // Cycle detection: check if this position is already in the beam's path
                const cycleIndex = beam.splitterPath.indexOf(posKey);
                if (cycleIndex !== -1) {
                    hasInfiniteLoop = true;
                    // Mark all non-digit splitters in the cycle
                    for (let i = cycleIndex; i < beam.splitterPath.length; i++) {
                        if (this.splitters.has(beam.splitterPath[i])) {
                            splittersInLoop.add(beam.splitterPath[i]);
                        }
                    }
                    continue;
                }

                // Safety fallback: stop if hit count is excessive
                if (hitCount > 4) {
                    hasInfiniteLoop = true;
                    if (isSplitter) {
                        splittersInLoop.add(posKey);
                    }
                    continue;
                }

                // Build path for child beams
                const newPath = [...beam.splitterPath, posKey];

                // Split into perpendicular directions
                const [dir1, dir2] = this.getPerpendicularDirections(beam.direction);

                for (const newDir of [dir1, dir2]) {
                    const nextPos = this.grid.move(beam.position, newDir);

                    if (!isInside(nextPos)) {
                        // Beam exits grid immediately - terminal beam
                        const exitBeamId = nextBeamId++;
                        beamParent.set(exitBeamId, beam.beamId);
                        beamSplitPosition.set(exitBeamId, posKey);
                        terminalBeams.push({
                            originPosition: { ...beam.position },
                            exitDirection: newDir,
                            beamId: exitBeamId
                        });
                    } else {
                        // Beam continues - create new beam with new ID
                        const newBeamId = nextBeamId++;
                        beamParent.set(newBeamId, beam.beamId);
                        beamSplitPosition.set(newBeamId, posKey);
                        segments.push({
                            from: { ...beam.position },
                            to: { ...nextPos },
                            distance: beam.distance,
                            beamId: newBeamId
                        });
                        activeBeams.push({
                            position: nextPos,
                            direction: newDir,
                            distance: beam.distance + 1,
                            beamId: newBeamId,
                            originPosition: { ...beam.position },
                            originDirection: newDir,
                            splitterPath: newPath,
                            gatePath: beam.gatePath
                        });
                    }
                }
            } else {
                // Continue straight - same beam ID
                const nextPos = this.grid.move(beam.position, beam.direction);

                if (!isInside(nextPos)) {
                    // Beam exits grid without hitting another splitter
                    // Mark this beam ID as terminal (don't draw its segments)
                    terminalBeamIds.add(beam.beamId);
                    // Record the terminal beam info for drawing the dot
                    terminalBeams.push({
                        originPosition: { ...beam.originPosition },
                        exitDirection: beam.originDirection,
                        beamId: beam.beamId
                    });
                } else {
                    segments.push({
                        from: { ...beam.position },
                        to: { ...nextPos },
                        distance: beam.distance,
                        beamId: beam.beamId
                    });
                    activeBeams.push({
                        position: nextPos,
                        direction: beam.direction,
                        distance: beam.distance + 1,
                        beamId: beam.beamId,
                        originPosition: beam.originPosition,
                        originDirection: beam.originDirection,
                        splitterPath: beam.splitterPath,
                        gatePath: beam.gatePath
                    });
                }
            }
        }

        // Sort digits by distance
        digitsHit.sort((a, b) => a.distance - b.distance);

        // Compute beam colors post-simulation
        // Find the last correct digit in the sequential chain from minDigit
        let lastCorrectDigit = -1;
        const correctDigitPositions = new Set<string>();
        let lastCorrectPosition = '';
        for (let i = 0; i < digitsHit.length; i++) {
            // A digit is "correct" only if it matches its expected rank AND is
            // strictly farther than the previous one — ties break the chain,
            // matching checkWin (no two digits may share a distance).
            const strictlyFurther = i === 0 || digitsHit[i].distance > digitsHit[i - 1].distance;
            if (digitsHit[i].digit === this.grid.minDigit + i && strictlyFurther) {
                lastCorrectDigit = digitsHit[i].digit;
                const pk = positionKey(digitsHit[i].position);
                correctDigitPositions.add(pk);
                lastCorrectPosition = pk;
            } else {
                break;
            }
        }

        // Build position → digit map from digitsHit
        const digitAtPosition = new Map<string, number>();
        for (const hit of digitsHit) {
            digitAtPosition.set(positionKey(hit.position), hit.digit);
        }

        // Assign colors: iterate beamIds in order (parents before children)
        const beamColor = new Map<number, string>();
        beamColor.set(initialBeamId, 'teal');
        for (let id = initialBeamId + 1; id < nextBeamId; id++) {
            const parentId = beamParent.get(id);
            const parentColor = parentId !== undefined ? (beamColor.get(parentId) ?? 'teal') : 'teal';
            const splitPos = beamSplitPosition.get(id);

            if (splitPos !== undefined) {
                const digit = digitAtPosition.get(splitPos);
                if (digit !== undefined) {
                    // Split at a digit
                    if (splitPos === lastCorrectPosition && lastCorrectDigit < this.grid.maxDigit) {
                        beamColor.set(id, 'amber');
                    } else if (!correctDigitPositions.has(splitPos)) {
                        beamColor.set(id, 'red');
                    } else {
                        // Earlier correct digit: inherit from parent
                        beamColor.set(id, parentColor);
                    }
                } else {
                    // Split at a splitter: inherit
                    beamColor.set(id, parentColor);
                }
            } else {
                // No split position (e.g., teleport): inherit
                beamColor.set(id, parentColor);
            }
        }

        this.cachedSimulation = {
            segments,
            digitsHit,
            allTerminated: activeBeams.length === 0 && !hasInfiniteLoop,
            hasInfiniteLoop,
            hasDigitHitTwice,
            digitHitTwice,
            terminalBeams,
            terminalBeamIds,
            splittersInLoop,
            teleportSegments,
            beamColor,
            beamParent,
            beamSplitPosition
        };

        return this.cachedSimulation;
    }

    checkWin(): boolean {
        const sim = this.simulate();

        if (!sim.allTerminated) return false;
        if (sim.hasInfiniteLoop) return false;
        if (sim.hasDigitHitTwice) return false;
        if (sim.digitsHit.length !== this.grid.digitCount) return false;

        // Check digits are hit in order
        for (let i = 0; i < this.grid.digitCount; i++) {
            if (sim.digitsHit[i].digit !== this.grid.minDigit + i) return false;
        }

        // Check distances are strictly increasing
        for (let i = 1; i < sim.digitsHit.length; i++) {
            if (sim.digitsHit[i].distance <= sim.digitsHit[i - 1].distance) {
                return false;
            }
        }

        return true;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const sim = this.simulate();
        const splitterCount = this.splitters.size;

        // Distance spread: how much farther the last digit sits than the first. Beams
        // are measured in distance travelled, not elapsed time — unlike the fill/ignition
        // games, whose secondary metric is a genuine time spread.
        const firstDistance = sim.digitsHit[0].distance;
        const lastDistance = sim.digitsHit[sim.digitsHit.length - 1].distance;
        const distanceSpread = lastDistance - firstDistance;

        return calculateScore(splitterCount, distanceSpread);
    }

    /** Scoring-rules overlay: distance spread (secondary) as an arc between the min/max
     *  digit. Splitters (primary) are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        const from = this.grid.getDigitPosition(this.grid.minDigit);
        const to = this.grid.getDigitPosition(this.grid.maxDigit);
        if (!from || !to) return {};
        const hit = this.simulate().digitsHit;
        const fromValue = Math.round(hit[0]?.distance ?? 0);
        const toValue = Math.round(hit[hit.length - 1]?.distance ?? 0);
        return { spreadArc: { from, to, fromValue, toValue, primary: false, badgeSize: 'sm' } };
    }

    getStatus(): string {
        const sim = this.simulate();
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;
        const boldAmber = (text: string) =>
            `<span style="color:#fbbf24;font-weight:bold">${text}</span>`;

        if (this.checkWin()) {
            return 'You win! All digits reached in order by distance!';
        }

        if (sim.hasInfiniteLoop) {
            return `${boldRed('Infinite loop')} detected. All beams must terminate at edges.`;
        }

        if (sim.hasDigitHitTwice) {
            return `${boldRed('Invalid')}: digit ${sim.digitHitTwice} is reached twice. ` +
                `Each digit must only be hit once.`;
        }

        if (sim.digitsHit.length === 0) {
            return 'Beam doesn\'t hit any digits. Place splitters to redirect it.';
        }

        const sortedByDistance = [...sim.digitsHit].sort((a, b) => a.distance - b.distance);

        // Two digits at one distance have no order between them, which makes the
        // correct-run count below meaningless for the pair — so this comes first.
        for (let i = 1; i < sortedByDistance.length; i++) {
            if (sortedByDistance[i].distance === sortedByDistance[i - 1].distance) {
                return `${boldRed('Invalid')}: digits ${sortedByDistance[i - 1].digit} and ` +
                    `${sortedByDistance[i].digit} are reached at the same distance. ` +
                    `Digits must be strictly ordered.`;
            }
        }

        // Check if digits are in correct order
        let correctCount = 0;
        for (let i = 0; i < sortedByDistance.length; i++) {
            if (sortedByDistance[i].digit === this.grid.minDigit + i) {
                correctCount++;
            } else {
                break;
            }
        }

        // The digit the run is missing: the first one the beams should reach but
        // don't, at this point in the distance order.
        const expected = this.grid.minDigit + correctCount;

        if (correctCount < this.grid.digitCount) {
            if (correctCount < sortedByDistance.length) {
                const wrongDigit = sortedByDistance[correctCount];
                return `${boldAmber('Warning')}: digit ${expected} should be hit before digit ${wrongDigit.digit}.`;
            }
            return `Place splitters to reach digit ${expected} next.`;
        }

        return 'Place splitters to guide the beam through all digits.';
    }

    isStarted(): boolean {
        return this.splitters.size > 0;
    }

    /**
     * Serialize game state to string format.
     * Format: "A2,B4,C6" - comma-separated positions
     */
    serialize(): string {
        const entries: string[] = [];
        for (const key of this.splitters) {
            const [row, col] = key.split(',').map(Number);
            entries.push(this.gs.serializePosition({ row, col }));
        }
        return entries.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        for (const entry of splitEntries(serialized, 2)) {
            try {
                const pos = this.gs.deserializePosition(entry);
                if (!this.grid.hasDigitAt(pos)) {
                    this.splitters.add(positionKey(pos));
                }
            } catch {
                // Invalid position, skip
            }
        }
        this.cachedSimulation = null;
    }
}
